import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="chat-widget-offset"
//
// Raises ONLY the blue circular chat launcher off the bottom edge, so it stops
// colliding with bottom-anchored page UI (e.g. the sticky "Update" bar).
//
// Why this is so awkward: the vendor's <chat-widget> custom element is
// position:static with height 0 — everything visible lives in its (open) shadow
// root. Page CSS can't cross the shadow boundary, and the widget's own
// `:host { all: initial }` kills inherited custom properties, so a
// `--chat-widget-bottom` style var wouldn't reach it either. The vendor exposes
// ~30 CSS custom properties (colors, width, height) but none for vertical
// offset, and the loader script takes no position data-attribute. Injecting a
// <style> into the shadow root is the only lever available.
//
// The collapsed launcher and the prompt card share one fixed container, so they
// can only move together. The shadow root nests them like so:
//
//   div.lc_text-widget                position:fixed bottom:20px  ← container
//     div.lc_text-widget--prompt                    ← "have a question?" card
//     div.lc_text-widget--box                       ← EXPANDED PANEL
//     button.lc_text-widget--bubble   position:fixed bottom:20px  ← blue circle
//
// Two earlier attempts each moved exactly one of them and broke the other:
//   * offsetting the container alone pushed the expanded panel up off the bottom
//     edge, leaving a visible gap, and left the launcher at its own fixed 20px;
//   * offsetting the button alone slid the launcher up UNDERNEATH the prompt
//     card, which stayed with the container and painted over it.
//
// So both must move together while collapsed, and neither while expanded. The
// vendor toggles `data-active="true"` on the <chat-widget> host when the panel
// opens, which gives us a hook to scope the offset to the collapsed state only:
// the expanded panel keeps the vendor's intended flush-to-bottom position.
//
// ⚠️ VENDOR RISK: this depends on LeadConnector's internal class name
// (.lc_text-widget--bubble) and on their shadow root staying `open`. Either can
// change without notice in a vendor update. Everything here is best-effort and
// cosmetic — if the selector stops matching or the shadow root goes closed, the
// launcher simply sits at its default 20px again. It must never throw.
export default class extends Controller {
  // Collapsed-launcher distance from the bottom of the viewport, all viewports.
  // Change here. Applies only while collapsed — see the note above.
  static OFFSET = "96px"
  static STYLE_ID = "stubs-chat-widget-offset"

  connect() {
    if (this.apply()) return

    // The third-party script injects <chat-widget> and builds its shadow root
    // seconds after DOMContentLoaded, so watch for it rather than giving up.
    this.observer = new MutationObserver(() => {
      if (this.apply()) this.stop()
    })
    this.observer.observe(document.body, { childList: true, subtree: true })

    // ponytail: hard stop so pages where the widget never loads (hide_chat_widget?)
    // don't keep an observer alive forever.
    this.timer = setTimeout(() => this.stop(), 30000)
  }

  disconnect() {
    this.stop()
  }

  stop() {
    if (this.observer) this.observer.disconnect()
    this.observer = null
    clearTimeout(this.timer)
  }

  // Returns true once the style is in place (or was already).
  apply() {
    try {
      const root = document.querySelector("chat-widget")?.shadowRoot
      if (!root) return false
      if (root.getElementById(this.constructor.STYLE_ID)) return true

      const style = document.createElement("style")
      style.id = this.constructor.STYLE_ID
      // :host([data-active="true"]) is the expanded panel — excluded so it stays
      // flush against the bottom edge exactly as the vendor positions it.
      const o = this.constructor.OFFSET
      style.textContent = `
        :host(:not([data-active="true"])) .lc_text-widget,
        :host(:not([data-active="true"])) .lc_text-widget--bubble {
          bottom: ${o} !important;
        }
      `
      root.appendChild(style)
      return true
    } catch {
      // Cosmetic only — never break the page over a chat bubble.
      return true
    }
  }
}
